// src/pages/TopicDetailPage.js
// Trang một chuyên đề: phần lý thuyết đóng khung ở trên (nếu có), bên dưới là toàn bộ bài tập kèm lời giải,
// xếp theo dạng (thứ tự dạng như khi soạn), trong mỗi dạng từ dễ đến khó.
import React, { useEffect, useRef, useState, useContext } from "react";
import { BookOpen, ListChecks, Lock } from "lucide-react";
import { useParams } from "react-router-dom";
import { AppContext } from "../AppContext";
import ShareLinkButton, { topicLink } from "../components/ShareLinkButton";
import { useLearner } from "../LearnerContext";
import { contentFetch, recordActivity } from "../learnerApi";
import {
  ExerciseCard,
  CONTENT_STYLES,
  groupByType,
  hasText,
  typeset,
  useExerciseMarks,
} from "../components/LessonContentModal";

const LEVEL_ORDER = { easy: 0, medium: 1, hard: 2 };

// Sắp bài tập: giữ thứ tự xuất hiện của từng dạng, trong dạng xếp Dễ → Trung bình → Khó.
// Bỏ các bài chưa có đề.
const orderExercises = (exercises) => {
  const list = (exercises || []).filter((ex) => hasText(ex.content));
  const typeIndex = new Map();
  list.forEach((ex) => {
    if (!typeIndex.has(ex.exerciseType)) typeIndex.set(ex.exerciseType, typeIndex.size);
  });
  return list
    .map((ex, index) => ({ ex, index }))
    .sort(
      (a, b) =>
        typeIndex.get(a.ex.exerciseType) - typeIndex.get(b.ex.exerciseType) ||
        (LEVEL_ORDER[a.ex.level] ?? 3) - (LEVEL_ORDER[b.ex.level] ?? 3) ||
        a.index - b.index
    )
    .map(({ ex }) => ex);
};

const TopicDetailPage = () => {
  const { API_BASE_URL } = useContext(AppContext);
  const { id } = useParams();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef(null);
  // Chưa mở khoá học viên thì máy chủ chỉ gửi tên chuyên đề và số bài (topic.contentLocked)
  const { learner, openGate } = useLearner();
  const marks = useExerciseMarks(topic && !topic.contentLocked ? `topic:${topic.id}:` : null);

  useEffect(() => {
    const fetchTopic = async () => {
      try {
        const res = await contentFetch(`${API_BASE_URL}/topics/${id}`);
        if (!res.ok) {
          throw new Error("Failed to fetch topic");
        }
        setTopic(await res.json());
      } catch (err) {
        console.error("Lỗi tải chi tiết chuyên đề:", err);
        setTopic(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchTopic();
  }, [id, API_BASE_URL, learner?.id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Ghi việc học: học sinh mở chuyên đề (đã có nội dung)
  useEffect(() => {
    if (topic && !topic.contentLocked) {
      recordActivity({ type: "open_topic", refKey: `topic:${topic.id}`, title: `Chuyên đề: ${topic.title}` });
    }
  }, [topic]);

  // Hiện công thức toán sau khi có nội dung
  useEffect(() => {
    if (topic) typeset(contentRef.current);
  }, [topic]);

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-green-600 mx-auto mb-5"></div>
        <p className="text-2xl text-gray-700">Đang tải chi tiết chuyên đề...</p>
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="text-center py-20">
        <p className="text-2xl text-red-600">Không tìm thấy chuyên đề.</p>
      </div>
    );
  }

  if (topic.contentLocked) {
    return (
      <div className="container mx-auto px-4 py-6 lg:py-10 max-w-2xl text-center">
        {topic.grade && (
          <span className="inline-block text-sm font-semibold text-green-700 bg-green-100 rounded-full px-3 py-0.5 mb-2">
            Lớp {topic.grade}
          </span>
        )}
        <h1 className="text-xl lg:text-3xl font-extrabold text-gray-900 mb-4">{topic.title}</h1>
        <div className="bg-white border rounded-2xl shadow-md p-5 lg:p-8">
          <Lock size={40} className="mx-auto text-indigo-500 mb-3" />
          <p className="text-gray-700 mb-1">
            Chuyên đề gồm {topic.hasTheory ? "phần lý thuyết và " : ""}
            <b>{topic.exerciseCount} bài tập</b>
            {topic.exerciseTypeCount ? ` thuộc ${topic.exerciseTypeCount} dạng` : ""}, có lời giải chi tiết.
          </p>
          <p className="text-gray-600 text-sm mb-5">
            Nội dung dành cho học viên đang theo học tại trung tâm Mây Xanh.
          </p>
          <button
            onClick={() => openGate()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-full px-6 py-2.5"
          >
            Nhập SĐT phụ huynh để học
          </button>
        </div>
      </div>
    );
  }

  const hasTheory = hasText(topic.theory) || /<img/i.test(topic.theory || "");
  const groups = groupByType(orderExercises(topic.exercises));
  const exerciseCount = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <div ref={contentRef} className="container mx-auto px-4 py-4 lg:py-10 max-w-4xl">
      <div className="text-center mb-5 lg:mb-8">
        {topic.grade && (
          <span className="inline-block text-sm font-semibold text-green-700 bg-green-100 rounded-full px-3 py-0.5 mb-2">
            Lớp {topic.grade}
          </span>
        )}
        <h1 className="text-xl lg:text-4xl font-extrabold text-gray-900">{topic.title}</h1>
        <div className="flex justify-center mt-2">
          <ShareLinkButton
            url={topicLink(topic.id)}
            title={`Chuyên đề: ${topic.title}`}
            label="Chia sẻ chuyên đề"
            className="text-sm border border-gray-300 bg-white rounded-full px-3 py-1.5"
          />
        </div>
      </div>

      {hasTheory && (
        <section className="bg-white border-2 border-green-200 shadow-md rounded-2xl p-4 lg:p-6 mb-6">
          <h2 className="flex items-center text-lg lg:text-xl font-bold text-green-700 mb-3">
            <BookOpen size={22} className="mr-2" />
            Lý thuyết
          </h2>
          <div
            className="text-gray-700 leading-relaxed lesson-doc exercise-html overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: topic.theory }}
          />
        </section>
      )}

      <section>
        <h2 className="flex items-center text-lg lg:text-xl font-bold text-gray-800 mb-1">
          <ListChecks size={22} className="mr-2 text-indigo-600" />
          Bài tập
        </h2>
        {exerciseCount === 0 ? (
          <p className="text-gray-500 py-6">Chuyên đề này chưa có bài tập.</p>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4">
              {exerciseCount} bài · {groups.length} dạng · sắp xếp từ dễ đến khó, bấm "Xem lời giải"
              để xem bài giải
            </p>
            {groups.map((group, i) => (
              <div key={i} className="mb-6">
                <h3 className="font-bold text-indigo-700 mb-2">
                  Dạng {i + 1}: {group.type}
                </h3>
                {group.items.map((ex) => (
                  <ExerciseCard
                    key={`${ex.id || ex._id}-${ex.number}`}
                    exercise={ex}
                    tracking={{
                      refKey: `topic:${topic.id}:`,
                      title: `Chuyên đề: ${topic.title}`,
                      marks,
                    }}
                  />
                ))}
              </div>
            ))}
          </>
        )}
      </section>
      <style>{CONTENT_STYLES}</style>
    </div>
  );
};

export default TopicDetailPage;
